/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/camelcase */
import { Grid, makeStyles } from '@material-ui/core';
import TableItems, {
  MenuActionItem,
  MenuActions,
  TableCell,
  TableRow,
} from 'components/TableItems';
import i18n from 'i18n';

import {
  getAllApplicationsPlatformRequest,
  removeClientsApplicationsPlatformRequest,
} from 'modules/environments/actions';
import { modalOpen, modalClose } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import Permission from 'components/ValidPermission';
import { useParams } from 'react-router';
import {
  EnvironmentFields,
  ListPlatformAppsFields,
  ListPlatformAppsProps,
} from 'modules/environments/interfaces';
import RefreshButton from 'components/RefreshButton';
import { useTableFilters } from 'hooks/useTableFilters';
import { SearchBar } from 'components/SearchBar';
import Button from 'components/Button';
import styles from '../styles';
import ListApplicationClients from './ListApplicationClients';
import TitleBehavior from '../FormBehavior/TitleBehavior';

const useStyles = makeStyles(styles);

const initialQuery = {
  offset: 0,
  limit: 25,
  search: '',
};

interface Props {
  applicationsPlatformList: ListPlatformAppsProps;
  isLoadingApps: boolean;
  environmentSelected: EnvironmentFields | null;
  getAllApplicationsPlatformRequest: ({ id, query }) => void;
  modalClose: () => void;
  modalOpen: (props: ModalFields) => void;
  removeClientsApplicationsPlatformRequest: ({ id, code, query }) => void;
}

const ListPlatformApplicationsEnvironment = ({
  applicationsPlatformList,
  isLoadingApps,
  environmentSelected,
  getAllApplicationsPlatformRequest,
  modalClose,
  modalOpen,
  removeClientsApplicationsPlatformRequest,
}: Props) => {
  const classes = useStyles();

  const {
    query,
    searchValue,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters(initialQuery);

  const [refresh, setRefresh] = React.useState(false);
  const { id } = useParams<{ id: string }>();

  const getEnvironmentApplications = (refresh = false) => {
    getAllApplicationsPlatformRequest({
      id,
      query,
    });

    setRefresh(refresh);
  };

  React.useEffect(() => {
    getEnvironmentApplications();
  }, [getAllApplicationsPlatformRequest, query]);

  const handleRemoveApp = (item: ListPlatformAppsFields) =>
    modalOpen({
      title: i18n.t('REMOVE_APP_NAME_KEY', {
        app_name: item?.name,
      }),
      content: i18n.t('REMOVE_APP_CONFIRMATION_KEY', {
        app_name: item?.name,
      }),
      footer: (
        <Grid>
          <Button
            deleteButton
            id="remove-button"
            onClick={() => {
              removeClientsApplicationsPlatformRequest({
                id,
                code: item?.code,
                query,
              });
              modalClose();
            }}
          >
            {i18n.t('REMOVE_APP_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });

  return (
    <>
      <TitleBehavior
        title={i18n.t('ENVIRONMENT_APPS_LISTING_KEY')}
        subtitle={i18n.t('ENVIRONMENT_APPS_LISTING_SUBTITLE_KEY')}
      />
      <Grid container alignItems="center" className={classes.actionsHeader}>
        <Grid container xs={6}>
          <SearchBar
            id="search-clients"
            isLoading={isLoadingApps}
            searchValue={searchValue}
            handleSearch={handleSearch}
            setSearchValue={setSearchValue}
          />
        </Grid>
        <Grid container xs={6} justify="flex-end">
          <RefreshButton
            id="btn-refresh"
            onClick={() => getEnvironmentApplications()}
            animate={isLoadingApps}
            disabled={isLoadingApps}
          />
        </Grid>
      </Grid>
      <Grid>
        <TableItems
          header={
            <>
              <TableCell
                handleOrder={() => handleOrder('name')}
                order
                className={classes.rowAllRowsPlatform}
              >
                {i18n.t('NAME_KEY')}
              </TableCell>
              <TableCell
                handleOrder={() => handleOrder('code')}
                order
                align="center"
                centerTitle
                className={classes.rowAllRowsCount}
              >
                {i18n.t('CODE_KEY')}
              </TableCell>
              <TableCell
                handleOrder={() => handleOrder('client_count')}
                order
                align="center"
                centerTitle
                className={classes.rowAllRowsCount}
              >
                {i18n.t('APPLICATIONS_CLIENTS_COUNT_KEY')}
              </TableCell>

              <TableCell centerTitle className={classes.actionHeader}>
                <Grid>{i18n.t('ACTIONS_KEY')}</Grid>
              </TableCell>
            </>
          }
          renderItems={(item: ListPlatformAppsFields, index) => (
            <TableRow key={item?.name}>
              <TableCell className={classes.rowAllRowsPlatform}>
                {item?.name}
              </TableCell>
              <TableCell
                align="center"
                centerTitle
                className={classes.rowAllRowsCount}
              >
                {item?.code}
              </TableCell>
              <TableCell className={classes.rowAllRowsCount}>
                {item?.client_count}
              </TableCell>
              <TableCell align="center">
                <MenuActions id={`btn-options-apps-${index}`}>
                  <MenuActionItem
                    id="btn-app-details"
                    onClick={() =>
                      modalOpen({
                        title: i18n.t('APPLICATIONS_CLIENTS_MODAL_TITLE_KEY', {
                          app_name: item?.name,
                        }),
                        content: (
                          <ListApplicationClients
                            environmentName={environmentSelected?.name}
                            appCode={item?.code}
                            appName={item?.name}
                          />
                        ),
                      })
                    }
                  >
                    {i18n.t('LOGS_PAGE.DETAILS_KEY')}
                  </MenuActionItem>
                  <Permission value="remove_applications_environment">
                    <MenuActionItem
                      id="btn-app-remove"
                      onClick={() => handleRemoveApp(item)}
                    >
                      {i18n.t('REMOVE_APP_KEY')}
                    </MenuActionItem>
                  </Permission>
                </MenuActions>
              </TableCell>
            </TableRow>
          )}
          data={applicationsPlatformList}
          isLoading={!refresh && isLoadingApps}
          onChangePage={handlePageChange}
          onChangeLimit={handleLimitChange}
          notFoundText={i18n.t('APPLICATIONS_PAGE.NO_APPLICATIONS_FOUND_KEY')}
        />
      </Grid>
    </>
  );
};

const mapStateToProps = ({
  environments: {
    isLoadingApps,
    applicationsPlatformList,
    environmentSelected,
  },
  authenticate: { companySelected, data },
}: ApplicationState) => ({
  companySelected,
  role: data?.role,
  applicationsPlatformList,
  isLoadingApps,
  environmentSelected,
});

export default connect(mapStateToProps, {
  getAllApplicationsPlatformRequest,
  modalOpen,
  modalClose,
  removeClientsApplicationsPlatformRequest,
})(ListPlatformApplicationsEnvironment);
